import { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Navigation, Scrollbar } from 'swiper';

import { useRouter } from '@/hooks/useRouter';
import { getNewsPages } from '@/lib/getNewsPages';
import { paths } from '@/lib/paths';
import { commonMessages } from '@/messages/common';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { useRegionProvider } from '@/providers/RegionProvider';
import { getBreakpointSize } from '@/theme';
import { theme } from '@/theme/getTheme';
import type { NewsPageType } from '@/views/NewsView/types';
import { sortNewsByPublishDateDesc } from '@/views/NewsView/utils/sort';

import * as S from './LatestArticles.styles';

export type LatestArticlesProps = {
  currentSlug: string;
  newsPages: NewsPageType[];
};

export const LatestArticles = () => {
  const { formatMessage } = useIntl();
  const {
    country: { slug },
    locale,
  } = useRegionProvider();

  const draftMode = useContext(DraftModeContext);

  const [newsPages, setNewsPages] = useState<NewsPageType[] | []>([]);

  useEffect(() => {
    async function fetchNewsPages() {
      const fetchedNewsPages = await getNewsPages(
        locale.locale,
        slug,
        draftMode,
      );
      setNewsPages(fetchedNewsPages);
    }

    fetchNewsPages();
  }, [locale, slug, draftMode]);

  const { query } = useRouter();

  const currentSlug = query.slug
    ? Array.isArray(query.slug)
      ? query.slug.join('/')
      : query.slug
    : '';

  const sortedNewsPages = sortNewsByPublishDateDesc(newsPages);
  const latest4News = sortedNewsPages.slice(0, 4);
  const filteredNews = latest4News.filter((item) => item.slug !== currentSlug);

  if (filteredNews.length < 4) {
    const additionalNews = sortedNewsPages.find(
      (item) => item.slug !== currentSlug && !latest4News?.includes(item),
    );
    if (additionalNews) {
      filteredNews.push(additionalNews);
    }
  }

  if (filteredNews.length === 0) {
    return null;
  }
  const slidesPerView = filteredNews.length;
  const mobileSlidesPerView = 1.2;
  const spaceBetween = theme.spacing(2);

  const defaultBreakpoints = {
    [getBreakpointSize('xs')]: {
      slidesPerView: mobileSlidesPerView,
    },
    [getBreakpointSize('sm')]: {
      slidesPerView: slidesPerView,
    },
    [getBreakpointSize('md')]: {
      slidesPerView: slidesPerView,
    },
    [getBreakpointSize('lg')]: {
      slidesPerView: slidesPerView,
    },
  };

  const getHeight = (len: number) => {
    switch (len) {
      case 1:
        return 450;
      case 2:
        return 336;
      case 3:
        return 332;
      case 4:
      default:
        return 246;
    }
  };
  const imgHeight = getHeight(filteredNews.length);
  return (
    <>
      <S.Header>
        <S.Text>{formatMessage(commonMessages.latestArticles)}</S.Text>
        <S.Discover
          href={paths.news.asPath()}
          isUpperCase={false}
          label={formatMessage(commonMessages.discoverAllNews)}
          variant='link'
        >
          <S.Chevron />
        </S.Discover>
      </S.Header>
      <S.Wrapper>
        {filteredNews.length > 1 ? (
          <S.StyledSwiper
            $hideNavigation={true}
            breakpoints={defaultBreakpoints}
            data-test-id='NewSliderStyledSwiper'
            modules={[Navigation, Scrollbar]}
            navigation
            scrollbar={true}
            separator='false'
            slidesPerView={mobileSlidesPerView}
            spaceBetween={spaceBetween}
            watchSlidesProgress
          >
            {filteredNews.map((item) => (
              <S.SwiperSlide key={item.sys?.id}>
                <S.ArticlePreview
                  key={item.sys?.id}
                  data={item}
                  height={imgHeight}
                />
              </S.SwiperSlide>
            ))}
          </S.StyledSwiper>
        ) : (
          <S.ArticlePreview
            $single={true}
            data={filteredNews[0]}
            height={imgHeight}
            showButton={true}
          />
        )}
      </S.Wrapper>
    </>
  );
};

export default LatestArticles;
